import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import {
  pointsDetailsSubtitleStyle,
  pointsDetailsTitleStyle
} from "../../components/styles/commonStyles";
import axios from "axios";
import { getFormattedDate } from "../../Utils/commonFunction";

interface MemberPointsDetailsPageProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  businessId: string;
  establishmentName?: string;
}

interface PointsDetailsData {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  Points: number;
  DateCreated: string;
  SourceName: string;
}

const apiEndpoint: any = process.env.REACT_APP_API_BASE_URL;

const MemberPointsDetailsPage = ({
  open,
  onClose,
  userId,
  businessId,
  establishmentName = ""
}: MemberPointsDetailsPageProps) => {
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState<PointsDetailsData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const fetchPoints = useCallback(
    (page: number = pageNumber, size: number = pageSize) => {
      if (!userId || !businessId) return;
      setLoading(true);

      const token = localStorage.getItem("businessAccessToken");
      const url = `${apiEndpoint}/loyaltyprogram/businessportal/points/user/${userId}/business/${businessId}?PageSize=${size}&PageNumber=${page}`;

      axios
        .get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        })
        .then((response) => {
          const resData = response?.data || {};
          const mappedData = (resData.Data || []).map((item: any) => ({
            PatronId: item.PatronId,
            MerchantId: item.MerchantId,
            EstablishmentName: item.EstablishmentName,
            Points: item.Points,
            DateCreated: item.DateCreated,
            SourceName: item.SourceName
          }));
          setPoints(mappedData);
          setTotalCount(resData?.TotalCount || resData?.totalCount || 0);
          if (typeof resData?.PageNumber === "number") {
            setPageNumber(resData.PageNumber);
          } else if (typeof resData?.pageNumber === "number") {
            setPageNumber(resData.pageNumber);
          } else {
            setPageNumber(page);
          }
          if (typeof resData?.PageSize === "number") {
            setPageSize(resData.PageSize);
          } else if (typeof resData?.pageSize === "number") {
            setPageSize(resData.pageSize);
          } else {
            setPageSize(size);
          }
        })
        .catch(() => {
          setPoints([]);
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    },
    [userId, businessId, pageNumber, pageSize]
  );

  useEffect(() => {
    if (open) {
      fetchPoints();
    }
  }, [open, fetchPoints]);

  const handleClose = () => {
    onClose();
  };

  const handleRefresh = () => {
    fetchPoints(pageNumber, pageSize);
  };

  const handleChangeRowsPerPage = (val: any) => {
    const size = Number(val?.target?.value ?? val) || 10;
    setPageSize(size);
    setPageNumber(1);
    fetchPoints(1, size);
  };

  const handleChangePage = (e: any, val: any) => {
    const page = Math.max(1, (typeof val === "number" ? val : 0) + 1); // Convert from 0-based (UI) to 1-based (API)
    setPageNumber(page);
    fetchPoints(page, pageSize);
  };

  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Points",
      label: "Points",
      minWidth: 100,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "DateCreated",
      label: "Created At",
      minWidth: 180,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "SourceName",
      label: "Source Name",
      minWidth: 120,
      align: "left",
      format: (value: any) => value ?? "-"
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="h5" component="div" sx={pointsDetailsTitleStyle}>
            Point Details
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={pointsDetailsSubtitleStyle}
          >
            {establishmentName}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={handleRefresh}
            disabled={loading}
            size="small"
            title="Refresh"
          >
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <CustomTableWithPagination
          columns={columns}
          data={points}
          apiStatus={loading ? "fetching" : "idle"}
          payloadSize={pageSize}
          currentPage={pageNumber - 1}
          totalCount={totalCount}
          lastEvaluatedKey={null}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          isShowPagination={true}
          noDataFoundMessage="No points details found."
        />
      </DialogContent>
    </Dialog>
  );
};

export default MemberPointsDetailsPage;
